import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

export const Seo = () => {
  const { pathname } = useLocation();

  const DEFAULT_SEO_CONFIG = {
    category: '',
    domain: 'https://cdri.pro',
    url: '',
    title: 'CDRI - 뷰티 IT 솔루션 프로바이더 씨디알아이',
    desc: 'Create a Better Beauty Industry! CDRI는 더 나은 뷰티 산업을 만듭니다',
    h1: 'CDRI - 뷰티 IT 솔루션 프로바이더 씨디알아이',
  };

  const currentPageSeoConfig =
    SEO_CONFIGS.find(({ url }) => url === pathname) || DEFAULT_SEO_CONFIG;

  return (
    <>
      <Helmet>
        {/* 기본 SEO */}
        <title>{currentPageSeoConfig.title}</title>
        <meta name="description" content={currentPageSeoConfig.desc} />

        {/* Open Graph */}
        <meta property="og:title" content={currentPageSeoConfig.title} />
        <meta property="og:description" content={currentPageSeoConfig.desc} />
        <meta
          property="og:url"
          content={currentPageSeoConfig.domain + currentPageSeoConfig.url}
        />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentPageSeoConfig.title} />
        <meta name="twitter:description" content={currentPageSeoConfig.desc} />
      </Helmet>
      <h1 className="a11y-hidden">{currentPageSeoConfig.h1}</h1>
    </>
  );
};

const SEO_CONFIGS: Array<{
  category: string;
  domain: string;
  url: string;
  title: string;
  desc: string;
  h1: string;
}> = [
  {
    category: '전체',
    domain: 'https://cdri.pro',
    url: '/faq/',
    title:
      '화장품 수출 FAQ 총정리 | 절차, 인증, 품질관리, 시장 진입, 솔루션 안내',
    desc: '화장품 수출을 준비 중이신가요? 수출 절차, 국가별 인증(CPNP, MoCRA, CDSCO), 품질관리, 시장 진입 전략, 자동화 솔루션(CERTICOS, CDRI)까지—실무에 필요한 모든 정보를  확인하세요.',
    h1: 'FAQ',
  },
  {
    category: '화장품 수출',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-guide',
    title: '화장품 수출 가이드 | 절차, 관세 등 필수 정보 총정리',
    desc: '화장품을 해외로 수출할 때 꼭 알아야 할 절차, 필수 서류, 관세, 성분 규제, 바이어 찾기, 물류, 마케팅 전략 등 핵심 FAQ를 한눈에 확인하세요.',
    h1: '화장품 수출',
  },
  {
    category: '화장품 수출 인증 및 인허가',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-certification-and-licensing',
    title: '화장품 수출 인증 가이드 | CPNP, MoCRA, CDSCO 등 인허가 절차 총정리',
    desc: 'CPNP, MoCRA, CDSCO 등 국가별 화장품 수출 인증 절차부터 소요 시간, 비용, 필수 서류, 오류 해결법까지 인허가에 대한 모든 정보를 한눈에 확인하세요.',
    h1: '화장품 수출 인증 및 인허가',
  },
  {
    category: '미국 수출 관련 MoCRA',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-to-the-us-mocra',
    title: 'MoCRA 인증 가이드 | 미국 화장품 수출을 위한 필수 절차 총정리',
    desc: 'MoCRA란 무엇인지부터 인증 절차, 소요 시간, 비용, 테스트 방식, 갱신 주기, FDA와의 차이점까지—미국 화장품 수출을 위한 MoCRA 핵심 정보를 모두 확인하세요.',
    h1: '미국 수출 관련 MoCRA',
  },
  {
    category: '유럽 수출 관련 CPNP',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-to-europe-cpnp',
    title: 'CPNP 등록 가이드 | 유럽 화장품 수출을 위한 필수 절차 총정리',
    desc: 'CPNP란 무엇인지부터 등록 절차, 필요 서류, 소요 시간, 비용, 테스트 방식, 오류 해결법까지—유럽 화장품 수출을 위한 CPNP 필수 정보를 한눈에 확인하세요.',
    h1: '유럽 수출 관련 CPNP',
  },
  {
    category: '인도 수출 관련 CDSCO',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-to-india-cdsco',
    title: 'CDSCO 인증 가이드 | 인도 화장품 수출을 위한 절차 총정리',
    desc: 'CDSCO란 무엇이며, 인도 화장품 수출을 위한 인증 절차, 필요 서류, 소요 시간, 비용, 제품 테스트, 오류 해결법까지—CDSCO 관련 필수 정보를 한 번에 확인하세요.',
    h1: '인도 수출 관련 CDSCO',
  },
  {
    category: '화장품 품질 관리',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-quality-assurance',
    title: '화장품 품질 관리 가이드 | GMP, 임상시험, 성분 안정성 총정리',
    desc: '화장품 제조 시 필수 품질 관리 지침부터 GMP 절차, 제품 테스트, 성분 안정성 평가, 임상시험 준비와 비용까지—화장품 품질 관리 FAQ를 한눈에 확인하세요.',
    h1: '화장품 품질 관리',
  },
  {
    category: '서류 및 준비 절차',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-documentation',
    title: '화장품 수출 서류 및 준비 절차 가이드 | 필수 문서 총정리',
    desc: '화장품 수출 시 필요한 서류 목록부터 샘플 관리, 번역 및 인증서 발급, 세관 통관 서류, 시험 성적서 사용 국가까지—수출 준비 FAQ를 한 번에 확인하세요.',
    h1: '서류 및 준비 절차',
  },
  {
    category: 'CERTICOS 특장점 및 활용법',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-solution-certicos',
    title:
      'CERTICOS 화장품 수출 인증 솔루션 | 자동화, 비용 절감, 실시간 관리까지',
    desc: '화장품 수출 인증 자동화 솔루션 CERTICOS. 인증 시간 단축, 비용 절감, 실시간 진행 상황 확인, 다국가 인증 지원 등 주요 기능과 활용법을 FAQ로 확인하세요.',
    h1: 'CERTICOS 특장점 및 활용법',
  },
  {
    category: '시장 진입 전략',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-market-entry-strategy',
    title: '화장품 해외 시장 진입 전략 FAQ | 바이어, 마케팅, 인사이트 총정리',
    desc: '해외 바이어 발굴, 시장 동향 조사, 디지털 마케팅, 로컬 협력, 법적 이슈까지—화장품 수출 시 꼭 알아야 할 시장 진입 전략을 FAQ 형식으로 정리했습니다.',
    h1: '시장 진입 전략',
  },
  {
    category: '기타 궁금증',
    domain: 'https://cdri.pro',
    url: '/faq/cosmetic-export-additional-qna',
    title:
      '화장품 해외 시장 진입 전략 FAQ | 자동화, 검역, 지원사업 등 기타 정보',
    desc: 'CDRI 프로그램의 적합 대상, 고객 지원, 비용 효율성, 검역 샘플 관리, 수출 시 실수, 바우처 활용 등 화장품 수출 전반의 기타 FAQ를 제공합니다.',
    h1: '기타 궁금증',
  },
];
